import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from 'recharts';
import { Download, Search, RefreshCw } from 'lucide-react';
import { ReportHeader } from '../../components/ReportHeader';
import { ExportButton } from '../../components/ExportButton';
import { CollapsibleSection } from '../../components/CollapsibleSection';

const API = process.env.REACT_APP_BACKEND_URL;
const PALETTE = ['#006cbe', '#6440fb', '#04aa6d', '#f59e0b', '#ef4444', '#8b5cf6', '#0891b2', '#d97706', '#dc2626', '#7c3aed'];

/* ── helpers ─────────────────────────────────────────── */
const fmtDate = d => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return d; }
};

const truncate = (s, n = 22) => (!s ? '—' : s.length > n ? s.slice(0, n) + '…' : s);

/* ── small building blocks ───────────────────────────── */
function StatCard({ label, value, color = '', sub, testId }) {
  return (
    <div className="stat-card" data-testid={testId || `allup-stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="stat-label">{label}</div>
      <div className={`stat-value ${color}`}>{(value ?? 0).toLocaleString()}</div>
      {sub && <div className="stat-sub">{sub}</div>}
    </div>
  );
}

const ChartCard = ({ title, children, span = 1 }) => (
  <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10,
    padding: '16px 20px', gridColumn: `span ${span}` }}>
    <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', marginBottom: 12 }}>{title}</div>
    {children}
  </div>
);

const EmptyChart = ({ msg = 'No data' }) => (
  <div style={{ height: 160, display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: '#94a3b8', fontSize: 12 }}>{msg}</div>
);

function HBar({ data, labelKey, valueKey, color = '#006cbe', height = 200 }) {
  if (!data?.length) return <EmptyChart />;
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} layout="vertical" margin={{ left: 0, right: 20, top: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" horizontal={false} />
        <XAxis type="number" tick={{ fontSize: 10 }} />
        <YAxis type="category" dataKey={labelKey} tick={{ fontSize: 10 }} width={110}
          tickFormatter={v => truncate(v, 16)} />
        <Tooltip formatter={(v) => v.toLocaleString()} />
        <Bar dataKey={valueKey} fill={color} radius={[0, 3, 3, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

function VBar({ data, labelKey, valueKey, color = '#006cbe', height = 200 }) {
  if (!data?.length) return <EmptyChart />;
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} margin={{ left: 0, right: 10, top: 0, bottom: 40 }}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey={labelKey} tick={{ fontSize: 9 }} angle={-35} textAnchor="end"
          tickFormatter={v => truncate(v, 14)} interval={0} />
        <YAxis tick={{ fontSize: 10 }} />
        <Tooltip formatter={(v) => v.toLocaleString()} />
        <Bar dataKey={valueKey} fill={color} radius={[3, 3, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

function DonutChart({ data, labelKey, valueKey, height = 200 }) {
  if (!data?.length) return <EmptyChart />;
  return (
    <ResponsiveContainer width="100%" height={height}>
      <PieChart>
        <Pie data={data} dataKey={valueKey} nameKey={labelKey} cx="50%" cy="50%"
          innerRadius={50} outerRadius={75} paddingAngle={3}>
          {data.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
        </Pie>
        <Tooltip formatter={(v) => v.toLocaleString()} />
        <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 10 }} />
      </PieChart>
    </ResponsiveContainer>
  );
}

function GroupedRegionChart({ enrollData, compData, height = 260 }) {
  const data = useMemo(() => {
    const map = {};
    (enrollData || []).forEach(r => { map[r.region] = { region: r.region, enrollments: r.enrollments || 0, completions: 0 }; });
    (compData || []).forEach(r => {
      if (map[r.region]) map[r.region].completions = r.completions || 0;
      else map[r.region] = { region: r.region, enrollments: 0, completions: r.completions || 0 };
    });
    return Object.values(map).sort((a, b) => b.enrollments - a.enrollments);
  }, [enrollData, compData]);

  if (!data.length) return <EmptyChart />;
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} margin={{ left: 0, right: 10, top: 0, bottom: 50 }}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="region" tick={{ fontSize: 9 }} angle={-35} textAnchor="end"
          tickFormatter={v => truncate(v, 14)} interval={0} />
        <YAxis tick={{ fontSize: 10 }} />
        <Tooltip formatter={(v) => v.toLocaleString()} />
        <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 10 }} />
        <Bar dataKey="enrollments" name="Enrollments" fill="#006cbe" radius={[3, 3, 0, 0]} />
        <Bar dataKey="completions" name="Completions" fill="#04aa6d" radius={[3, 3, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

function StatusBadge({ status }) {
  const style = status === 'Completed'
    ? { background: '#dcfce7', color: '#166534' }
    : { background: '#fef3c7', color: '#92400e' };
  return <span style={{ ...style, padding: '2px 7px', borderRadius: 4, fontSize: 11, fontWeight: 600 }}>{status}</span>;
}

function Pagination({ page, totalPages, total, pageSize, onPage }) {
  if (totalPages <= 1) return null;
  const start = Math.max(1, page - 2), end = Math.min(totalPages, page + 2);
  const nums = [];
  for (let i = start; i <= end; i++) nums.push(i);
  return (
    <div className="pagination" data-testid="allup-pagination">
      <span className="pagination-info">
        {((page - 1) * pageSize + 1).toLocaleString()}–{Math.min(page * pageSize, total).toLocaleString()} of {total.toLocaleString()}
      </span>
      <button className="page-btn" disabled={page <= 1} onClick={() => onPage(1)}>«</button>
      <button className="page-btn" disabled={page <= 1} onClick={() => onPage(page - 1)}>‹</button>
      {nums.map(n => <button key={n} className={`page-btn${n === page ? ' active' : ''}`} onClick={() => onPage(n)}>{n}</button>)}
      <button className="page-btn" disabled={page >= totalPages} onClick={() => onPage(page + 1)}>›</button>
      <button className="page-btn" disabled={page >= totalPages} onClick={() => onPage(totalPages)}>»</button>
    </div>
  );
}

/* ── main component ──────────────────────────────────── */
export default function AllUpReport() {
  const [summary, setSummary]               = useState(null);
  const [charts, setCharts]                 = useState(null);
  const [chartsLoading, setChartsLoading]   = useState(false);
  const [rows, setRows]                     = useState([]);
  const [total, setTotal]                   = useState(0);
  const [totalPages, setTotalPages]         = useState(1);
  const [page, setPage]                     = useState(1);
  const [tableLoading, setTableLoading]     = useState(false);
  const [filterOptions, setFilterOptions]   = useState({ regions: [], partners: [] });
  const [piiAllowed, setPiiAllowed]         = useState(false); // fail-closed default

  const [filters, setFilters] = useState({
    region: '', partner_name: '', domain: '', learner_type: '',
    learner_status: '', platform_type: '', from_date: '', to_date: '', search: '',
  });
  const [applied, setApplied] = useState({ ...filters });
  const searchRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/api/reports/filters`, { withCredentials: true })
      .then(r => setFilterOptions(r.data)).catch(console.error);
    fetchSummary({});
    fetchCharts({});
    fetchTable(1, {});
    // Read pii_allowed from session
    axios.get(`${API}/api/auth/me`, { withCredentials: true })
      .then(r => setPiiAllowed(r.data?.pii_allowed === true))
      .catch(() => setPiiAllowed(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchSummary = async (f) => {
    try {
      const r = await axios.get(`${API}/api/reports/allup/summary`, { params: f, withCredentials: true });
      setSummary(r.data);
    } catch (e) { console.error(e); }
  };

  const fetchCharts = async (f) => {
    setChartsLoading(true);
    try {
      const r = await axios.get(`${API}/api/reports/allup/charts`, { params: f, withCredentials: true });
      setCharts(r.data);
    } catch (e) { console.error(e); }
    finally { setChartsLoading(false); }
  };

  const fetchTable = useCallback(async (p, f) => {
    setTableLoading(true);
    try {
      const r = await axios.get(`${API}/api/reports/allup/list`,
        { params: { ...f, page: p, page_size: 50 }, withCredentials: true });
      setRows(r.data.data || []);
      setTotal(r.data.total || 0);
      setTotalPages(r.data.total_pages || 1);
      setPage(p);
    } catch (e) { console.error(e); }
    finally { setTableLoading(false); }
  }, []);

  const handleApply = () => {
    setApplied({ ...filters });
    fetchSummary(filters);
    fetchCharts(filters);
    fetchTable(1, filters);
  };

  const handleReset = () => {
    const blank = { region: '', partner_name: '', domain: '', learner_type: '',
      learner_status: '', platform_type: '', from_date: '', to_date: '', search: '' };
    setFilters(blank);
    setApplied(blank);
    fetchSummary({});
    fetchCharts({});
    fetchTable(1, {});
  };

  const upd = (k, v) => setFilters(f => ({ ...f, [k]: v }));

  const s = summary || {};
  const c = charts || {};

  return (
    <div data-testid="allup-report-page">
      <ReportHeader
        title="All-Up LevelUp"
        subtitle="Consolidated view of learners, enrollments and completions across all programmes"
      />

      {/* ── KPI Summary — always visible ── */}
      <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)', marginBottom: 20 }}>
        <StatCard label="Registered Learners" value={s.total_users} color="green" testId="allup-stat-registered" />
        <StatCard label="Enrolled Learners" value={s.enrolled_users} color="green" testId="allup-stat-enrolled" />
        <StatCard label="Total Enrollments" value={s.total_enrollments} testId="allup-stat-enrollments" />
        <StatCard label="Total Completions" value={s.total_completions} color="green" testId="allup-stat-completions" />
        <StatCard label="At-Risk Enrollments" value={s.at_risk_enrollments} color="red" testId="allup-stat-at-risk"
          sub="Enrolled 23–30 days, not completed" />
      </div>

      {/* ── Analytics section — expanded by default ── */}
      <CollapsibleSection title="Analytics" defaultExpanded={true}>
        {chartsLoading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <RefreshCw size={20} style={{ animation: 'spin 1s linear infinite', color: '#9CA3AF' }} />
            <p style={{ color: '#9CA3AF', fontSize: 12, marginTop: 8 }}>Loading analytics…</p>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16 }}>

            {/* Learner Analytics */}
            <ChartCard title="Learners by Registration Platform Source">
              <HBar data={c.by_platform_source} labelKey="platform_type" valueKey="count" color="#6440fb" />
            </ChartCard>
            <ChartCard title="Top 5 Popular Domains">
              <HBar data={c.top_domains} labelKey="domain" valueKey="count" color="#0891b2" />
            </ChartCard>
            <ChartCard title="Top 10 Partners by Learner Count">
              <HBar data={c.top_partners} labelKey="partner_name" valueKey="learners" height={260} />
            </ChartCard>

            {/* Course Analytics */}
            <ChartCard title="Course Type Distribution (by Enrollments)">
              <DonutChart data={c.course_type_dist} labelKey="course_type" valueKey="enrollments" />
            </ChartCard>
            <ChartCard title="Top 5 Popular Projects">
              <VBar data={c.top_projects} labelKey="program_name" valueKey="completions" color="#04aa6d" height={220} />
            </ChartCard>
            <ChartCard title="Top 5 Solution Areas by Enrollments">
              <VBar data={c.top_solution_areas} labelKey="solution_area" valueKey="enrollments" color="#f59e0b" height={220} />
            </ChartCard>
            <ChartCard title="Top 10 Popular Courses by Enrollments" span={2}>
              <HBar data={c.top_courses} labelKey="course_name" valueKey="enrollments" height={280} color="#6440fb" />
            </ChartCard>
            {c.top_languages?.length > 0 && (
              <ChartCard title="Top 5 Course Languages">
                <DonutChart data={c.top_languages} labelKey="language" valueKey="courses" />
              </ChartCard>
            )}

            {/* Regional Analytics — grouped bar */}
            <ChartCard title="Enrollments &amp; Completions by Region" span={2}>
              <GroupedRegionChart
                enrollData={c.enrollments_by_region}
                compData={c.completions_by_region}
                height={260}
              />
            </ChartCard>

            {/* Partner Type Analytics */}
            <ChartCard title="Top 50 CSP Partners by Learner Count">
              <HBar data={c.top_csp_partners} labelKey="partner_name" valueKey="learners" color="#0891b2" />
            </ChartCard>
            <ChartCard title="Top Partners by Completion Count">
              <HBar data={c.top_enrolled_partners} labelKey="partner_name" valueKey="learners" color="#8b5cf6" />
            </ChartCard>
          </div>
        )}
      </CollapsibleSection>

      {/* ── Data section — collapsed by default ── */}
      <CollapsibleSection title="Data" defaultExpanded={false}>
        {/* Filters live inside Data section */}
        <div className="filter-bar" style={{ flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
          <div className="filter-group">
            <label className="filter-label">Region</label>
            <select className="filter-select" data-testid="allup-filter-region"
              value={filters.region} onChange={e => upd('region', e.target.value)}>
              <option value="">All Regions</option>
              {(filterOptions.regions || []).map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div className="filter-group">
            <label className="filter-label">Partner</label>
            <select className="filter-select" data-testid="allup-filter-partner"
              value={filters.partner_name} onChange={e => upd('partner_name', e.target.value)}>
              <option value="">All Partners</option>
              {(filterOptions.partners || []).map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div className="filter-group">
            <label className="filter-label">Domain</label>
            <input type="text" className="filter-input" placeholder="e.g. microsoft.com"
              data-testid="allup-filter-domain" value={filters.domain}
              onChange={e => upd('domain', e.target.value)} />
          </div>
          <div className="filter-group">
            <label className="filter-label">Learner Status</label>
            <select className="filter-select" data-testid="allup-filter-learner-type"
              value={filters.learner_type} onChange={e => upd('learner_type', e.target.value)}>
              <option value="">All Learners</option>
              <option value="Active">Active Only</option>
              <option value="Retired">Retired Only</option>
            </select>
          </div>
          <div className="filter-group">
            <label className="filter-label">Enrol Status</label>
            <select className="filter-select" data-testid="allup-filter-enrol-status"
              value={filters.learner_status} onChange={e => upd('learner_status', e.target.value)}>
              <option value="">All</option>
              <option value="completed">Completed</option>
              <option value="enrolled">In Progress</option>
            </select>
          </div>
          <div className="filter-group">
            <label className="filter-label">From</label>
            <input type="date" className="filter-input" data-testid="allup-filter-from"
              value={filters.from_date} onChange={e => upd('from_date', e.target.value)} />
          </div>
          <div className="filter-group">
            <label className="filter-label">To</label>
            <input type="date" className="filter-input" data-testid="allup-filter-to"
              value={filters.to_date} onChange={e => upd('to_date', e.target.value)} />
          </div>
          <div className="filter-group">
            <label className="filter-label">Search</label>
            <div style={{ position: 'relative' }}>
              <Search size={12} style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: '#9CA3AF' }} />
              <input type="text" className="filter-input" ref={searchRef}
                placeholder="Name or email…" data-testid="allup-filter-search"
                value={filters.search} onChange={e => upd('search', e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleApply()}
                style={{ paddingLeft: 26 }} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end' }}>
            <button className="btn btn-primary" onClick={handleApply} data-testid="allup-apply-btn">Apply</button>
            <button className="btn btn-secondary" onClick={handleReset} data-testid="allup-reset-btn">Reset</button>
          </div>
        </div>

        {/* Enrollment Detail Table */}
        <div className="table-container">
          <div className="table-header">
            <div>
              <span className="table-title">Enrollment Detail</span>
              <span className="table-count" style={{ marginLeft: 8 }}>({total.toLocaleString()} records)</span>
            </div>
            <ExportButton
              requestUrl="/api/reports/allup/export/request"
              params={applied}
              filename="allup_report.csv"
              testId="allup-export-btn"
            />
          </div>

          {tableLoading ? (
            <div style={{ padding: 40, textAlign: 'center' }}>
              <RefreshCw size={20} style={{ animation: 'spin 1s linear infinite', color: '#9CA3AF' }} />
            </div>
          ) : rows.length === 0 ? (
            <div className="empty-state">
              <h3>No enrollment records found</h3>
              <p>Try adjusting your filters</p>
            </div>
          ) : (
            <>
              <div className="table-scroll">
                <table data-testid="allup-table">
                  <thead>
                    <tr>
                      <th style={{ minWidth: 120 }}>LMS ID</th>
                      {piiAllowed && <th>Learner Name</th>}
                      {piiAllowed && <th>Email</th>}
                      <th>Partner</th>
                      <th>Region</th>
                      <th>Country</th>
                      <th>Domain</th>
                      <th>Type</th>
                      <th>Course Name</th>
                      <th>Course Type</th>
                      <th>Solution Area</th>
                      <th>Enrolled At</th>
                      <th>Completed At</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, i) => (
                      <tr key={i} data-testid={`allup-row-${i}`}>
                        <td style={{ fontFamily: 'monospace', fontSize: 12, whiteSpace: 'nowrap' }}>
                          {row.student_user_id || '—'}
                        </td>
                        {piiAllowed && (
                          <td style={{ maxWidth: 160, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {row.first_name || row.last_name ? `${row.first_name || ''} ${row.last_name || ''}`.trim() : '—'}
                          </td>
                        )}
                        {piiAllowed && (
                          <td style={{ maxWidth: 200, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                            title={row.email}>{row.email || '—'}</td>
                        )}
                        <td>{row.partner_name || '—'}</td>
                        <td>{row.region || '—'}</td>
                        <td>{row.country || '—'}</td>
                        <td style={{ maxWidth: 120, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.domain || '—'}</td>
                        <td>{row.learner_type || '—'}</td>
                        <td style={{ maxWidth: 220, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                          title={row.course_name}>{row.course_name || '—'}</td>
                        <td>{row.course_type || '—'}</td>
                        <td>{row.solution_area || '—'}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(row.enrolled_at)}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(row.completed_at)}</td>
                        <td><StatusBadge status={row.status} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Pagination page={page} totalPages={totalPages} total={total} pageSize={50}
                onPage={p => fetchTable(p, applied)} />
            </>
          )}
        </div>
      </CollapsibleSection>
    </div>
  );
}
